export type ReviewMurphyBand = 'high' | 'mid' | 'low';

export type BookingReviewUiStatus =
    | 'form'
    | 'thank_you'
    | 'pending'
    | 'declined'
    | 'approved';

export type ExistingReviewStatus = 'pending' | 'declined' | 'approved';

/** Matches BE `Rating` model status constants. */
export const RatingApiStatus = {
    PENDING_APPROVAL: 1,
    BLOCKED: 2,
    PUBLISHED: 3,
} as const;

export type ExistingBookingRating = {
    id: number;
    status: ExistingReviewStatus;
    /** Overall stars 1–5 for display. */
    overallStars: number;
};

export type ReviewCategory = {
    id: string;
    label: string;
};

/** Shape of each entry in city-adapter `publicSettings.ratingConfigs`. */
export type RatingConfigEntry = {
    name?: string | null;
    type?: string;
    required?: boolean;
};

export const DEFAULT_REVIEW_CATEGORIES: ReviewCategory[] = [
    { id: 'wifi', label: 'Wifi' },
    { id: 'amenities', label: 'Amenities' },
    { id: 'location', label: 'Location' },
    { id: 'social_community', label: 'Social & Community' },
];

/** Free-text field lives in the comment textarea, not as a star row. */
const NON_STAR_RATING_KEYS = new Set(['comment']);

export function humanizeRatingKey(key: string): string {
    return key
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * Map city-adapter `ratingConfigs` → star categories for the review form.
 * Skips `comment` (handled by the textarea). Prefers localized `name` from BE.
 */
export function mapRatingConfigsToCategories(
    ratingConfigs: Record<string, RatingConfigEntry> | null | undefined
): ReviewCategory[] {
    if (!ratingConfigs || typeof ratingConfigs !== 'object') return [];

    return Object.entries(ratingConfigs)
        .filter(([key]) => !NON_STAR_RATING_KEYS.has(key))
        .map(([id, config]) => {
            const localized =
                typeof config?.name === 'string' ? config.name.trim() : '';
            return {
                id,
                label: localized || humanizeRatingKey(id),
            };
        });
}

/** API stores overall rating on 0–100; UI uses 1–5 stars. */
export function apiScaleToStars(apiRating: number | undefined | null): number {
    if (apiRating == null || Number.isNaN(Number(apiRating))) return 0;
    return Math.min(5, Math.max(0, Math.round(Number(apiRating) / 20)));
}

export function mapRatingApiStatus(
    status: number | undefined | null
): ExistingReviewStatus | null {
    if (status === RatingApiStatus.PENDING_APPROVAL) return 'pending';
    if (status === RatingApiStatus.BLOCKED) return 'declined';
    if (status === RatingApiStatus.PUBLISHED) return 'approved';
    return null;
}

export function getReviewMurphyBand(stars: number): ReviewMurphyBand {
    // Initial / unset rating uses the same happy Murphy as 5 stars.
    if (stars <= 0) return 'high';
    if (stars >= 5) return 'high';
    if (stars >= 3) return 'mid';
    return 'low';
}

export function getMurphyIconSrc(band: ReviewMurphyBand): string {
    switch (band) {
        case 'high':
            return '/assets/icons/5.svg';
        case 'mid':
            return '/assets/icons/3-4.svg';
        case 'low':
            return '/assets/icons/2-1.svg';
    }
}
